import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import { css } from "glamor";
import Carousel, { CommonProps, Components, CarouselProps } from "react-images";
import { format } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import { IProductMedia } from "constants/product-media";
import Image360 from "components/product-media/image-360";
import { IVideoRef, VideoPlayer } from "components/product-media/video-player";
import { makeStyles, Theme } from "@material-ui/core/styles";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { isTablet, MEDIA_TYPE_360, MEDIA_TYPE_IMAGE, MEDIA_TYPE_THREEDIUM, MEDIA_TYPE_VIDEO } from "constants/media";
import ManifestLoader from "components/product-media/components/manifest-loader";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import DownloadIcon from "components/icons/download-new";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { IProduct } from "records/item/item";
import { createProductRecord } from "helpers/product-helpers";
import { ContentLoading } from "components/content-loading";
import { DATATEST } from "constants/integration";
import theme from "styles/theme";
import { IStore } from "reducers";
import { connect } from "react-redux";
import { Async3DPlayerWrapper } from "./player-3d-async";
import Modal from "@material-ui/core/Modal";
import { IWatermarking } from "reducers/watermarking";
import WatermarkProvider from "components/product-media/watermark-provider";
/** Product Details panel size to show the panel with the lightbox (open the lightbox with the panel but not over it) */
export const PANEL_SIZE = theme.sidebar.width;
const DEFAULT_DISTANCE_FACTOR_FULLSCREEN = 1.3;
// reduced high resolution image size to decrease processing time for watermarking
const WATERMARKING_SIZE = "W";
interface ILightBoxProps {
    media: IProductMedia[];
    open: boolean;
    onClose: (event?: React.SyntheticEvent<unknown>) => void;
    currentIndex?: number;
    panelWidth?: number; // reserve place to display a right panel with the lightbox modal
    onViewChange?: (index: number) => void;
    /** Show images in infinite loop. Default to true */
    infinite?: boolean;
    dataLoading?: boolean;
}

interface ILightBoxStateProps {
    viewport: string;
    touchDevice: boolean;
    scope: IScope;
}

interface IProductMediaLightBoxStateProps {
    watermarking?: IWatermarking;
}

interface IProductLightBoxProps extends Omit<ILightBoxProps, "media">, IProductMediaLightBoxStateProps {
    product: IProduct;
    scope?: IScope;
    media?: IProductMedia[];
    onSetMedia?: (media: IProductMedia[]) => void;
}

interface IZoomState {
    scale: number;
    zoomIn(): void;
    zoomOut(): void;
    resetTransform(event?: React.SyntheticEvent<unknown>): void;
}

const MediaLightBoxComponent: FC<ILightBoxProps & ILightBoxStateProps> = (props) => {
    const { media, currentIndex = 0, open, onClose, panelWidth, touchDevice, viewport, scope } = props;
    const { infinite = true, onViewChange, dataLoading } = props;
    const [zoomReadyIndex, setZoomReadyIndex] = useState<number>(null); // required to trigger zoom controls update

    const disable3DRendering = useMemo(
        () => touchDevice && isTablet(viewport) && scope?.brand?.hasPreference("disable_mobile_browser_3d"),
        [touchDevice, viewport, scope],
    );

    const zoomState = useRef<IZoomState>(null);
    const setZoomState = useCallback(
        (state: IZoomState, index: number) => {
            setZoomReadyIndex(index);
            return (zoomState.current = state);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [zoomReadyIndex],
    );
    useEffect(() => {
        if (open) {
            zoomState.current = null;
            setZoomReadyIndex(-1);
        }
    }, [open]);

    const handleViewChange = useCallback(
        (index: number) => {
            onViewChange?.(index);
            zoomState.current?.resetTransform();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onViewChange, zoomState.current],
    );

    const trackProps = useMemo(
        () => ({ infinite: infinite && _.size(media) > 1, onViewChange: handleViewChange }),
        [media, infinite, handleViewChange],
    );
    const customComponents = useMemo<Components>(() => ({ View, Header }), []);
    const views = useMemo(
        () => _.map(media, (source: IProductMedia) => ({ caption: "", source, disable3DRendering })),
        [media, disable3DRendering],
    );
    const preventPropagation = useCallback((event: React.SyntheticEvent<unknown>) => event.stopPropagation(), []);

    const styles = useLightboxStyles(panelWidth);
    const modalStyles = useStyles({ margin: panelWidth });

    return (
        <ClickAwayListener onClickAway={onClose}>
            <Modal className={modalStyles.modal} onClick={preventPropagation} open={open} onClose={onClose}>
                <div className={modalStyles.modalContainer}>
                    <div className={modalStyles.content}>
                        <Carousel
                            styles={styles.carousel}
                            views={views as any} // internal view can get only string but we need to pass IMedia
                            currentIndex={currentIndex}
                            trackProps={trackProps}
                            components={customComponents}
                            // inner props to avoid not generic types
                            {...{
                                onClose: onClose,
                                zoomProps: { state: zoomState.current, setZoomState },
                                dataLoading,
                            }}
                        />
                    </div>
                </div>
            </Modal>
        </ClickAwayListener>
    );
};

const mapStateToProps = (store: IStore): ILightBoxStateProps => ({
    viewport: store.app.viewport,
    touchDevice: store.app.touchDevice,
    scope: store.scope,
});

export const MediaLightBox = connect<ILightBoxStateProps, null, ILightBoxProps>(mapStateToProps)(
    MediaLightBoxComponent,
);

/** Useful to encapsulate the logic of getting the media from a product */
const ProductMediaLightBoxComponent = ({ product, scope, watermarking, ...rest }: IProductLightBoxProps) => {
    const watermarkingEnabled = useMemo(
        () => scope.brand?.hasPreference("add_watermarking") && watermarking?.active,
        [scope.brand, watermarking?.active],
    );
    const media = useMemo<IProductMedia[]>(
        () => createProductRecord(product)?.getMedia(scope, watermarkingEnabled ? WATERMARKING_SIZE : null),
        [product, scope, watermarkingEnabled],
    );
    return <MediaLightBox media={media} {...rest} />;
};

const mapProductMediaStateToProps = (store: IStore): IProductMediaLightBoxStateProps => ({
    watermarking: store.watermarking.payload,
});

export const ProductMediaLightBox = connect<IProductMediaLightBoxStateProps, null, IProductLightBoxProps>(
    mapProductMediaStateToProps,
)(ProductMediaLightBoxComponent);

interface IZoomProps {
    state: IZoomState;
    setZoomState(ref: IZoomState, index: number): IZoomState;
}

interface ICustomCarouselProps extends CarouselProps {
    dataLoading?: boolean;
    zoomProps: IZoomProps;
    onClose: () => void;
}

interface IComponentProps extends CommonProps {
    data: {
        source: IProductMedia;
        caption?: string;
        disable3DRendering?: boolean;
    };
    carouselProps: ICustomCarouselProps;
    index: number;
}

const View = ({ data, currentView, currentIndex, carouselProps, index }: IComponentProps) => {
    const [loading, setLoading] = useState<boolean>(true);

    const isCurrent = useMemo<boolean>(() => data === (currentView as any), [data, currentView]); // currentView type could not be overridden to fix type
    const classes = useViewStyles({});
    const async3dplayerClasses = {
        statusMessage: classes.statusMessage3d,
        hint: classes.image3dHint,
        progressClasses: {
            root: classes.image3dProgress,
            label: classes.image3dProgressWhiteText,
            percents: classes.image3dProgressWhiteText,
        },
    };

    const player = useRef<IVideoRef>(null);
    useEffect(() => {
        if (player && player.current && data.source.type === MEDIA_TYPE_VIDEO && !isCurrent) {
            player.current.pause();
        }
    }, [data, currentView, player]); // eslint-disable-line react-hooks/exhaustive-deps

    const preventPropagation = useCallback((event: React.SyntheticEvent<unknown>) => event.stopPropagation(), []);

    const setZoomState = useCallback<(state: IZoomState) => IZoomState>(
        (state: IZoomState) => (isCurrent ? carouselProps.zoomProps.setZoomState(state, index) : state), // setting state for external zoom operations
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [index, carouselProps.zoomProps.setZoomState, isCurrent],
    );
    const withZoomState = useCallback(
        (f: (state: IZoomState) => React.ReactNode) => (state: IZoomState) => f(setZoomState(state)),
        [setZoomState],
    );

    const onImageLoad = useCallback(
        (state: IZoomState) => (event) => {
            if (!carouselProps.dataLoading) {
                setLoading(false);
            }

            state.resetTransform(event); // update position when an image is fully loaded
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setZoomState, carouselProps.dataLoading],
    );
    useEffect(() => {
        setLoading(carouselProps.dataLoading);
    }, [carouselProps.dataLoading]);

    const zoomOptions = useMemo<any>(
        () => ({ contentClass: classes.zoomComponent, wrapperClass: classes.zoomWrapper }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const zoomReset = useMemo(() => ({ animationTime: 0 }), []);
    const zoomDBClick = useMemo(() => ({ disabled: true }), []);
    return (
        <div data-test={DATATEST.ASSORTMENTS.PDP.TRANSFORM_COMPONENT} className={classes.wrapper}>
            {data.source.type === MEDIA_TYPE_IMAGE && (
                <TransformWrapper options={zoomOptions} reset={zoomReset} doubleClick={zoomDBClick}>
                    {withZoomState((props) => (
                        <WatermarkProvider active={index === currentIndex} imageUrl={data.source.source}>
                            {(src, isLoading) => (
                                <>
                                    {(isLoading || (isCurrent && loading)) && (
                                        <ContentLoading className={classes.loading} />
                                    )}
                                    <TransformComponent>
                                        {!isLoading && (
                                            <img
                                                className={classNames(classes.content, classes.image, {
                                                    loading: isLoading,
                                                })}
                                                src={src}
                                                alt={data.caption}
                                                onLoad={onImageLoad(props)}
                                            />
                                        )}
                                    </TransformComponent>
                                </>
                            )}
                        </WatermarkProvider>
                    ))}
                </TransformWrapper>
            )}
            {data.source.type === MEDIA_TYPE_360 && (
                <div className={classNames(classes.content, classes.content360)}>
                    {isCurrent && (
                        <ManifestLoader manifest={data.source.manifest} src={data.source.source}>
                            {(source, preloading) => <Image360 source={source} preloading={preloading} />}
                        </ManifestLoader>
                    )}
                    <span className={classes.caption}>{format("Click and drag left and right to rotate image")}</span>
                </div>
            )}
            {data.source.type === MEDIA_TYPE_VIDEO && (
                <div className={classes.content} onKeyDown={preventPropagation}>
                    <VideoPlayer
                        ref={player}
                        source={data.source.source}
                        poster={data.source.poster}
                        classes={{
                            poster: classes.videoThumbnail,
                            player: /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
                                ? classes.custom
                                : classes.player,
                        }}
                        autoPlay={data === (currentView as any)}
                    />
                </div>
            )}
            {/*
             * We should load only one threedium iframe at a time.
             * Multiple open threedium iframes will lead to crush in mobile.
             */}
            {data.source.type === MEDIA_TYPE_THREEDIUM && isCurrent && (
                <div className={classNames(classes.content, classes.content3D)} onKeyDown={preventPropagation}>
                    <Async3DPlayerWrapper
                        useFitToView={true}
                        threediumId={(data as any).source.threedium_id}
                        disableRendering={data.disable3DRendering}
                        distanceFactor={DEFAULT_DISTANCE_FACTOR_FULLSCREEN}
                        classes={async3dplayerClasses}
                    />
                </div>
            )}
        </div>
    );
};

const Header = (props: IComponentProps) => {
    const { getStyles, data, carouselProps } = props;
    const { zoomProps, onClose } = carouselProps;
    const classes = useHeaderStyles({});
    return (
        <div
            data-test={DATATEST.ASSORTMENTS.PDP.ZOOM_HEADER}
            className={classNames("header", classes.header)}
            {...css(getStyles("header" as any, props))}
        >
            {data.source.type === MEDIA_TYPE_IMAGE && (
                <>
                    {data.source.download && (
                        <a className={classes.button} href={data.source.download}>
                            <DownloadIcon className={classes.icon} />
                        </a>
                    )}
                    <IconButton
                        data-test={DATATEST.ASSORTMENTS.PDP.ZOOM_IN_BTN}
                        className={classes.button}
                        aria-label="zoom-in"
                        onClick={zoomProps.state?.zoomIn}
                    >
                        <ZoomInIcon className={classes.icon} />
                    </IconButton>
                    <IconButton
                        data-test={DATATEST.ASSORTMENTS.PDP.ZOOM_OUT_BTN}
                        className={classes.button}
                        aria-label="zoom-out"
                        onClick={zoomProps.state?.zoomOut}
                    >
                        <ZoomOutIcon className={classes.icon} />
                    </IconButton>
                </>
            )}
            <IconButton
                data-test={DATATEST.ASSORTMENTS.PDP.ZOOM_CLOSE_BTN}
                className={classes.button}
                aria-label="close"
                onClick={onClose}
            >
                <CloseIcon className={classes.icon} />
            </IconButton>
        </div>
    );
};

const useStyles = makeStyles<Theme, { margin?: string | number }>({
    modalContainer: {
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
    },
    content: {
        maxHeight: "calc(100vh - 120px)",
    },
    modal: ({ margin }) => ({
        zIndex: !margin ? ("10000!important" as any) : ("1010!important" as any),
        width: `calc(100vw - ${margin}px)`,
    }),
    loading: () => ({
        position: "absolute",
        width: "100%",
        padding: "24px 0",
        textAlign: "center" as "center",
    }),
});

const useLightboxStyles = (margin: number | string = 0) => {
    // glam styles
    return useMemo(
        () => ({
            carousel: {
                header: (base) => ({
                    ...base,
                    "& button": {
                        outline: "none !important",
                    },
                }),
                container: (base, state) => ({
                    ...base,
                    "& .react-images__view-wrapper": {
                        left: "0 !important",
                    },
                    "& .react-images__pager": {
                        width: state.isFullscreen ? "100vw" : `calc(100vw - ${margin}px) !important`,
                    },
                }),
                navigation: (base) => ({
                    ...base,
                    "& button": {
                        outline: "none !important",
                    },
                }),
                footer: (base) => ({
                    ...base,
                    color: "#cccccc",
                    paddingRight: 20,
                    position: "relative",
                    bottom: "-16px",
                }),
            },
        }),
        [margin],
    );
};
const useViewStyles = makeStyles({
    wrapper: {
        maxWidth: "100%",
        maxHeight: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    content: {
        maxWidth: "max(80%, calc(100% - 180px))", // reserve place for lightbox controls
        maxHeight: "calc(100vh - 120px)",
        "& img": {
            maxWidth: "100%",
            maxHeight: "calc(100vh - 120px)",
        },
        "&.loading": {
            opacity: "0",
        },
    },
    image: {
        width: "100%",
        height: "100%",
        maxWidth: "100%",
    },
    loading: {
        background: "transparent",
    },
    zoomWrapper: {
        width: "max(80%, calc(100% - 180px))",
        height: "calc(100vh - 120px)",
    },
    zoomComponent: {
        justifyContent: "center",
        objectFit: "contain",
        "& img": {
            objectFit: "contain",
        },
    },
    player: {
        "& video": {
            maxWidth: "100%",
            maxHeight: "calc(100vh - 120px)",
            width: "auto !important",
            height: "auto !important",
        },
    },
    custom: {
        "& video": {
            maxWidth: "100%",
            maxHeight: "calc(100vh - 120px)",
        },
    },
    content360: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "& img": {
            maxHeight: "calc(100vh - 155px)", // reserve place for lightbox controls
            margin: "auto",
        },
    },
    content3D: {
        width: "max(80%, calc(100% - 180px))", // reserve place for lightbox controls
        height: "calc(100vh - 120px)",
    },
    statusMessage3d: {
        filter: "invert(100%)",
    },
    image3dHint: {
        position: "absolute",
        left: 0,
        right: 0,
        width: "max-content",
        marginLeft: "auto",
        marginRight: "auto",
    },
    image3dWrapper: {
        color: "white",
    },
    image3dProgress: {
        width: "70%",
        maxWidth: 500,
    },
    image3dProgressWhiteText: {
        color: theme.palette.white,
    },
    caption: {
        marginTop: 12,
        fontFamily: "Lato, sans-serif",
        fontSize: 16,
        color: "white",
        textAlign: "center",
    },
    videoThumbnail: {
        height: "100%",
        width: "100%",
        objectFit: "contain",
        minWidth: 320,
        minHeight: 180,
    },
});

const useHeaderStyles = makeStyles({
    header: {
        background: "linear-gradient(rgba(0,0,0,0.33), rgba(0,0,0,0))",
        justifyContent: "flex-end",
    },
    button: {
        padding: 8,
        width: 44,
        height: 44,
        outline: "none !important",
    },
    icon: {
        width: 28,
        height: 28,
        transition: "color 200ms",
        color: "rgb(255, 255, 255, 0.75)",
        "&:hover": {
            color: "white",
        },
    },
});
